<template>
  <section id="reset-pro-password">
    <div
      v-if="loading"
      class="l-loading-container"
    >
      <img
        src="@shared/assets/img/general/loading.svg"
        alt="Loading"
      >
    </div>
    <div
      v-if="!loading"
      class="l-left-container"
    >
      <img
        class="l-image"
        src="@shared/assets/img/logo/smac-sensors.png"
      >
      <div class="l-slogan">
        <div>CHANGE IS</div>
        <div class="l-tm-container">
          IN THE AIR<span class="l-tm">&reg;</span>
        </div>
      </div>
    </div>
    <div
      v-if="!loading"
      class="l-right-container"
    >
      <div v-if="success">
        <img
          src="@shared/assets/img/general/pro-app-logo.svg"
          alt="Pro App Logo"
        >
        <div class="l-header">
          Your password was successfully updated!
        </div>
        <div class="l-sub-header">
          Please use your new password to login to SmartAC.com PRO app
        </div>
      </div>
      <div v-else>
        <div class="l-header">
          Create New Password
        </div>
        <div class="l-sub-header">
          Please enter the password you want to use
        </div>
        <div
          v-if="errorObj.resetPasswordErr"
          class="l-login-error"
        >
          {{ errorObj.resetPasswordErr }}
        </div>
        <form
          class="l-form-container"
          @submit.prevent="createPassword"
        >
          <div class="l-input-container">
            <div class="l-label-container">
              <label
                for="password"
                class="l-label"
              >
                New Password
              </label>
            </div>
            <div class="l-input-box-container">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="l-input"
                placeholder="8 characters, a capital letter, and a number"
              >
              <button
                class="l-show-button"
                @click.prevent="showPassword = !showPassword"
              >
                <img
                  v-if="!showPassword"
                  src="@shared/assets/img/general/show-password.png"
                >
                <img
                  v-else
                  src="@shared/assets/img/general/hide-password.png"
                >
              </button>
            </div>
            <div
              v-if="errorObj.passwordErr"
              class="error-text"
            >
              {{ errorObj.passwordErr }}
            </div>
          </div>
          <div class="l-input-container">
            <div class="l-label-container">
              <label
                for="confirmPassword"
                class="l-label"
              >
                Confirm Password
              </label>
            </div>
            <div class="l-input-box-container">
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="l-input"
                placeholder="8 characters, a capital letter, and a number"
              >
              <button
                class="l-show-button"
                @click.prevent="showConfirmPassword = !showConfirmPassword"
              >
                <img
                  v-if="!showConfirmPassword"
                  src="@shared/assets/img/general/show-password.png"
                >
                <img
                  v-else
                  src="@shared/assets/img/general/hide-password.png"
                >
              </button>
            </div>
            <div
              v-if="errorObj.confirmPasswordErr"
              class="error-text"
            >
              {{ errorObj.confirmPasswordErr }}
            </div>
          </div>
          <button
            class="l-button"
            type="submit"
          >
            <span v-if="!processing">RESET PASSWORD</span>
            <img
              v-else
              src="@shared/assets/img/general/loading-white.svg"
              class="spinner"
            >
          </button>
        </form>
        <a
          href="https://smartac.com"
          class="l-footer-container"
        >
          <img
            src="@shared/assets/img/logo/black-logo.png"
            class="logo"
            alt="SmartAC.com"
          >
        </a>
      </div>
    </div>
  </section>
</template>

<script>
import apiClient from '@shared/lib/general/apiClient';

import passwordValidation from '@shared/lib/errorCatch/passwordValidation';

export default {
  name: 'ResetProPasswordView',
  metaInfo: {
    title: 'SmartAC.com | Reset Password',
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      errorObj: {},
      processing: false,
      success: false,
      loading: true,
    };
  },
  mounted() {
    this.checkResetToken();
  },
  methods: {
    async createPassword() {
      const {
        password,
        confirmPassword,
      } = this;

      const error = {};
      if (!password) {
        error.passwordErr = 'Please enter password';
      } else if (passwordValidation(password).length > 0) {
        error.passwordErr = 'Please include 8 characters, a capital letter, and a number.';
      }
      if (!confirmPassword) {
        error.confirmPasswordErr = 'Please enter confirm password';
      } else if (password !== confirmPassword) {
        error.confirmPasswordErr = 'Confirm password does not match password';
      }

      this.errorObj = error;

      if (Object.keys(error).length === 0) {
        try {
          this.processing = true;

          const { data } = await apiClient({
            method: 'PUT',
            url: '/auth/technician/reset-password',
            data: {
              password,
              token: this.$route.params.token,
            },
          });

          if (data) {
            this.success = true;
          }
        } catch (err) {
          if (err.response) {
            const { data } = err.response;
            this.errorObj = {
              resetPasswordErr: data.message,
            };
          }
        }
      }
      this.processing = false;
    },
    async checkResetToken() {
      const {
        token,
      } = this.$route.params;
      this.loading = true;
      try {
        const { data } = await apiClient({
          method: 'GET',
          url: `/auth/reset-token/check/${token}`,
        });
        if (!data) {
          this.$router.replace('/expired-link/reset');
        }
      } catch (err) {
        this.$router.replace('/expired-link/reset');
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">

section#reset-pro-password {
  display: flex;
  width: 100%;
  height: 100%;
  background-color: $WHITE;

  .l-loading-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .l-left-container {
    background-color: $N110;
    flex: 1;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;

    @media (max-width: 768px) {
      display: none;
    }

    .l-image {
      width: 50%;
      margin-bottom: 50px;

      @media (max-width: 1440px) {
        width: 70%;
      }
    }

    .l-comfort-tm {
      position: absolute;
      bottom: 27px;
      color: $WHITE;
      font-size: 2rem;
      font-weight: 500;
      line-height: 3rem;
      right: 20px;

      .l-sm-tm {
        color: $N80;
        font-weight: bold;
      }
    }

    .l-slogan {
      width: 50%;
      font-size: 4.8125rem;
      color: $WHITE;
      font-weight: 800;
      line-height: 5rem;

      .l-tm-container {
        position: relative;

        .l-tm {
          font-size: 1rem;
          position: absolute;
          top: -24px;
        }
      }

      @media (max-width: 1550px) {
        font-size: 3.8rem;

        .l-tm-container {
          .l-tm {
            top: -15px;
          }
        }
      }

      @media (max-width: 1440px) {
        width: 70%;
      }
    }
  }

  .l-right-container {
    width: 680px;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 136px;

    @media (max-width: 768px) {
      padding: 0 100px;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    @media (max-width: 420px) {
      padding: 0 40px;
    }

    @media (max-width: 320px) {
      padding: 0 25px;
    }

    .l-header {
      font-size: 2.25rem;
      color: $N100;
      margin-bottom: 2vh;
    }

    .l-sub-header {
      font-size: 1.5rem;
      color: $N100;
      margin-bottom: 6vh;
    }

    .l-form-container {
      .input-container {
        margin-bottom: 2vh;
      }

      .l-input-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 2vh;

        .l-label-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;

          .l-label {
            font-size: 1rem;
            color: $N100;
            font-weight: 500;
            text-transform: uppercase;
          }
        }

        .l-input-box-container {
          position: relative;

          .l-input {
            width: 100%;
          }

          .l-show-button {
            position: absolute;
            padding-right: 9px;
            right: 2px;
            top: 2px;
            bottom: 2px;
            background-color: $WHITE;
            display: flex;
            align-items: center;

            img {
              height: 24px;
            }
          }
        }

        .l-input {
          height: 56px;
          border: 2px solid $N10;
          border-radius: 4px;
          padding-left: 12px;
          font-size: 1.25rem;

          &:focus {
            border: 2px solid $NBLUE80;
          }

          @media (max-width: 320px) {
            height: 40px;
          }
        }

        .error-text {
          font-size: 0.75rem;
          color: $TOMATO100;
        }
      }

      .l-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 56px;
        background-color: $NBLUE100;
        border-radius: 6px;
        color: $WHITE;
        font-size: 1rem;
        font-weight: 600;

        @media (max-width: 320px) {
          height: 40px;
        }
      }
    }

    .l-login-error {
      font-size: 0.75rem;
      color: $TOMATO100;
      text-align: center;
      margin-top: -4vh;
      margin-bottom: 1vh;
      white-space: pre;
    }

    .l-reset-success {
      font-size: 0.75rem;
      color: $BASIL100;
      text-align: center;
      margin-top: -4vh;
      margin-bottom: 1vh;
      white-space: pre;
    }

    .l-footer-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-top: 15vh;

      .logo {
        width: 184px;
        height: 21px;

        @media (max-width: 420px) {
          width: 120px;
          height: 15px;
        }

        @media (max-width: 320px) {
          width: 100px;
          height: 12px;
        }
      }
    }
  }
}
</style>
